import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { PlusIcon } from '@phosphor-icons/react'
import { ComponentProps, ReactNode } from 'react'

import { AnimatedItem } from '@/components/ui/animated-item'
import { Button } from '@/components/ui/button'
import type { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'

import * as S from './linked-column.styled'
import { getColumnAttributes, LIST_ATTRIBUTES } from './linked-columns.attributes'
import { LinkedRow } from './linked-row'
import { LinkedRowSkeleton } from './linked-row-skeleton'

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>

const SKELETON_ROWS = 4

interface LinkedColumnProps<T extends { id: number }> {
  // Note: without it the column has no add button (e.g. a fixed list)
  addLabel?: string
  dimmed?: boolean
  dndType: string
  emptyDescription: string
  emptyTitle: string
  getSubtitleEnd?: (item: T) => ReactNode
  getLeading?: (item: T) => ReactNode
  getMenuItems?: (item: T) => DropdownMenuItem[]
  getSubtitle: (item: T) => string
  getTitle: (item: T) => string
  items: T[]
  loading?: boolean
  // Note: set while the parent list has nothing selected - the column cannot add anything yet
  missingParent?: { description: string; hint: string; title: string }
  numbered?: boolean
  selectedId: number | null
  title: string
  onAdd?: VoidFunction
  onEdit?: (item: T) => void
  onDragActiveChange?: (active: boolean) => void
  // Note: without it the rows are not draggable and show no drag handle
  onReorder?: (fromIndex: number, toIndex: number) => void
  onSelect: (id: number) => void
}

export const LinkedColumn = <T extends { id: number }>({
  addLabel,
  dimmed = false,
  dndType,
  emptyDescription,
  emptyTitle,
  getSubtitleEnd,
  getLeading,
  getMenuItems,
  getSubtitle,
  getTitle,
  items,
  loading = false,
  missingParent,
  numbered = false,
  selectedId,
  title,
  onAdd,
  onDragActiveChange,
  onEdit,
  onReorder,
  onSelect,
}: LinkedColumnProps<T>) => {
  const handleDragEnd: DragEndHandler = ({ canceled, operation }) => {
    onDragActiveChange?.(false)
    const { source } = operation
    if (canceled || !source || !isSortable(source)) return

    const fromIndex = source.initialIndex
    const toIndex = source.index
    if (fromIndex === toIndex) return

    onReorder?.(fromIndex, toIndex)
  }

  return (
    <S.Column {...getColumnAttributes(dndType)}>
      <S.Header>
        <Typography as="h2" breakLines={1} color="black800" size="heading-tiny">
          {title}
        </Typography>
        {addLabel && (
          <Tooltip
            disabled={!missingParent}
            trigger={
              <Button disabled={!onAdd} size="small" onClick={onAdd}>
                {addLabel}
                <Icon icon={PlusIcon} />
              </Button>
            }
          >
            {missingParent?.hint}
          </Tooltip>
        )}
      </S.Header>

      <S.Panel $dimmed={dimmed}>
        <S.List {...LIST_ATTRIBUTES}>
          {loading && (
            <S.Skeletons>
              {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <LinkedRowSkeleton
                  key={index}
                  hasEdit={!!onEdit}
                  hasMenu={!!getMenuItems}
                  sortable={!!onReorder}
                />
              ))}
            </S.Skeletons>
          )}

          {!loading && items.length === 0 && (
            <S.EmptyWrapper>
              <AnimatedItem>
                <EmptyState
                  actions={
                    missingParent || !addLabel ? undefined : (
                      <Button disabled={!onAdd} size="small" onClick={onAdd}>
                        {addLabel}
                        <Icon icon={PlusIcon} />
                      </Button>
                    )
                  }
                  description={missingParent?.description ?? emptyDescription}
                  title={missingParent?.title ?? emptyTitle}
                />
              </AnimatedItem>
            </S.EmptyWrapper>
          )}

          {!loading && (
            <DragDropProvider
              onDragEnd={handleDragEnd}
              onDragStart={() => onDragActiveChange?.(true)}
            >
              {items.map((item, index) => (
                <LinkedRow
                  key={item.id}
                  dndId={`${dndType}-${item.id}`}
                  index={index}
                  leading={getLeading?.(item)}
                  menuItems={getMenuItems?.(item)}
                  position={numbered ? index + 1 : undefined}
                  selected={item.id === selectedId}
                  subtitle={getSubtitle(item)}
                  subtitleEnd={getSubtitleEnd?.(item)}
                  title={getTitle(item)}
                  sortable={!!onReorder}
                  onEdit={onEdit ? () => onEdit(item) : undefined}
                  onSelect={() => onSelect(item.id)}
                />
              ))}
            </DragDropProvider>
          )}
        </S.List>
      </S.Panel>
    </S.Column>
  )
}
